/* eslint-disable no-restricted-syntax */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTransition, animated } from 'react-spring';
import * as lib from '~/dependencies';
import { useDtmfSound, useControlSessions } from '~/hooks';
import * as NuacomComponent from '~/components';
import * as actionTypes from '~/store/actions/actionTypes';
import TransferTo from '~/components/ActiveCall/TransferTo';
import ConferenceNumber from '~/components/ActiveCall/ConferenceNumber';
import { integrationContactState } from '~/helpers/contactsIntegration';

import {
  Space,
  DottedDivisor,
  //  RecentContainer,
  DtmfContainer,
} from './styles';
import TerminatedCall from './TerminatedCall';

export default function DisplayCall(props) {
  const [idSelected, setIdSelected] = useState(null);
  const [cursor, setCursor] = useState(0);
  const [callIdIntegration, setCallIdIntegration] = useState();
  const setSoundDtmf = useDtmfSound();
  const controlSessions = useControlSessions();

  const sessionActive = useSelector(state => state.callCurrent.session);
  const callCurrents = useSelector(state => state.callCurrent.data);
  const integrationsContacts = useSelector(
    state => state.integrations.contacts
  );

  const [addCallNotes, setAddCallNotes] = useState(false);
  const [addTags, setAddTags] = useState(false);
  const [callInformations, setCallInformations] = useState(null);

  const [dtmfState, setDtmfState] = useState({});

  const callCurrentsMemo = useMemo(() => callCurrents, [callCurrents]);

  // FIXME: Is session.id === getHeader('Call-ID')? if yes, use callInformations.getFormattedCallId();
  const callId = callInformations?.id?.split('@')[0].replace('"', '');

  const tempSessionId =
    idSelected || (sessionActive && sessionActive.request.callId);

  const isTerminated =
    callInformations?.status === actionTypes.CALL_FINISHED ||
    callInformations?.status === actionTypes.CALL_CANCELED;

  /*
   * NOTE: We are using includes because for outgoing calls the callId might also includes tag
   * E.G: 8ltchs7e0jpl45vgoue2 (CallId) + 3jhfd1j2qr (TagId)
   */
  const contact = lib._.chain(integrationsContacts)
    .filter(
      c =>
        callId?.includes(c.callId) &&
        c?.contactState === integrationContactState.CREATED
    )
    .first()
    .value();

  // Define which contact name or number must be showed, from CRM integrations, extensions or phonebook.
  const displayName = callInformations?.getDisplayName(
    contact?.fullNameFormatted
  );

  const numberFrom = callInformations?.numberFrom;

  const hasDtmf = !!callInformations?.dtmf;
  const transation = useTransition(hasDtmf, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    delay: 200,
  });

  const selectId = useCallback(
    id => {
      const findIndex = callCurrentsMemo.findIndex(e => e.id === id);
      if (findIndex !== -1) {
        const sessionSelected = callCurrentsMemo[findIndex];
        const {
          waitingTransfer: waitingTransferCallId,
          session: { request },
        } = sessionSelected;

        const xOriginalCallId = request.getHeader('X-Original-Callid');
        const xCallId = request.getHeader('Call-ID');

        if (sessionSelected?.invite) {
          setCallIdIntegration(xCallId);
        } else {
          setCallIdIntegration(xOriginalCallId);
        }

        setCallInformations(sessionSelected);

        if (waitingTransferCallId) {
          const findWaitingIndex = callCurrentsMemo.findIndex(
            e => e.id === waitingTransferCallId
          );

          if (findWaitingIndex !== -1) {
            const sessionWaitingRequest =
              callCurrentsMemo[findWaitingIndex].session.request;
            if (sessionWaitingRequest.getHeader('X-Original-Callid')) {
              setIdSelected(
                sessionWaitingRequest.getHeader('X-Original-Callid')
              );
            }
          }
        } else if (xOriginalCallId) {
          setIdSelected(xOriginalCallId);
        } else if (xCallId) {
          setIdSelected(xCallId); // used by outgoing call
        }
      }
    },
    [callCurrentsMemo]
  );

  useEffect(() => {
    /**
     * FIXME: Refactor call terminated.
     * This is required due to the current terminated call structure
     * When the call is terminated, the tags and notes is not longer handled by the session callActions.
     * The following logic is just passing the state from session.callActions to component local useState once the call is terminated.
     * Reference: NWA-2055 for future call terminated refactor.
     */
    if (
      isTerminated &&
      callInformations?.id &&
      callInformations?.callActions &&
      (callInformations.callActions.tags || callInformations.callActions.notes)
    ) {
      setAddTags(callInformations.callActions.tags);
      setAddCallNotes(callInformations.callActions.notes);
      setCallInformations({
        ...callInformations,
        callActions: {
          ...callInformations.callActions,
          tags: false,
          notes: false,
        },
      });
    }
  }, [
    isTerminated,
    callInformations,
    setAddTags,
    setAddCallNotes,
    setCallInformations,
  ]);

  useEffect(() => {
    if (sessionActive) {
      selectId(sessionActive.id);
    } else if (callCurrentsMemo[0]?.invite) {
      selectId(callCurrentsMemo[0]?.session?.id);
    }
  }, [sessionActive, selectId, callCurrentsMemo]);

  function numberNormalize(number) {
    return String(number).replace(/[^0-9*#]+/, '');
  }

  function inputNumberHandler(number, action, lastDigit) {
    const numberFormated = numberNormalize(number);

    if (action !== 'erase') {
      if (lastDigit) {
        setSoundDtmf(numberNormalize(lastDigit));
      } else {
        setSoundDtmf(numberFormated[numberFormated.length - 1]);
      }
    }

    if (numberFormated !== null) {
      controlSessions(callInformations.id, 'dtmf', number, {
        erase: action === 'erase',
        lastDigit,
      });
    }
  }

  function backSpaceNumber() {
    if (callInformations.dtmfValue.length > 0) {
      inputNumberHandler(
        callInformations.dtmfValue.substring(
          0,
          callInformations.dtmfValue.length - 1
        ),
        'erase'
      );
    } else {
      controlSessions(callInformations.id, 'dtmf', '');
    }
  }

  function handleCursor(value) {
    setCursor(value);
  }

  function insertByPosition(number) {
    if (callId) {
      const dtmfDigits = dtmfState[callId]
        ? `${dtmfState[callId]}${number}`
        : `${number}`;
      setDtmfState({
        [callId]: dtmfDigits,
      });
    }

    const adjustCursor =
      cursor >= callInformations.dtmfValue.length
        ? callInformations.dtmfValue.length
        : cursor;

    setCursor(cursor + 1);

    const values =
      callInformations.dtmfValue.length === 0
        ? number
        : callInformations.dtmfValue.slice(0, adjustCursor) +
          number +
          callInformations.dtmfValue.slice(adjustCursor);

    inputNumberHandler(values, null, number);
  }

  function handleInputNumber(number) {
    const num = number[cursor];
    const erase = number.length < callInformations.dtmfValue.length;

    if (!erase) {
      controlSessions(callInformations.id, 'dtmfDigit', number, {
        lastDigit: num,
      });
    } else {
      controlSessions(callInformations.id, 'dtmf', number, {
        erase: true,
      });
    }
    if (number.length === 0) {
      setCursor(0);
    } else {
      setCursor(cursor + 1);
    }
  }

  return (
    <>
      <NuacomComponent.Header headerColor {...props}>
        {/* <Details className="statusDuringCall">
          <Check className="check-icon" />
          <span>{status}</span>
          {numberFrom && (
            <div className="otherInformations">
              {displayName && <span>{displayName}</span>}
              <span>{numberFrom}</span>
            </div>
          )}
        </Details> */}
      </NuacomComponent.Header>

      {isTerminated && (
        <TerminatedCall
          displayName={displayName}
          numberFrom={numberFrom}
          setAddTags={setAddTags}
          addTags={addTags}
          setAddCallNotes={setAddCallNotes}
          addCallNotes={addCallNotes}
          callId={callIdIntegration}
          contactId={callInformations?.contactId}
          callInformations={callInformations}
          {...props}
        />
      )}

      <Space
        height={20}
        complete={
          !!(
            callInformations &&
            !callInformations.finished &&
            callInformations.dtmf
          )
        }
      />

      <DtmfContainer>
        {transation &&
          transation((style, item) =>
            item ? (
              <animated.div style={style}>
                {callInformations &&
                  !callInformations.finished &&
                  callInformations.dtmf && (
                    <NuacomComponent.NumberField
                      value={dtmfState[callId]}
                      isDtmf={callInformations.dtmf}
                      changed={handleInputNumber}
                      disabled
                      backSpace={backSpaceNumber}
                      dtmfDismiss={() =>
                        controlSessions(
                          callInformations.id,
                          'dtmfAction',
                          false
                        )
                      }
                      callAction={() => {}}
                      cursor={handleCursor}
                      message="Enter option number"
                    />
                  )}

                {callInformations &&
                  !callInformations.finished &&
                  callInformations.dtmf && (
                    <NuacomComponent.DialNumber
                      insertByPosition={insertByPosition}
                      handleKeyPress={() => {}}
                    />
                  )}
              </animated.div>
            ) : (
              ''
            )
          )}
      </DtmfContainer>

      {tempSessionId && callInformations && !callInformations.dtmf && (
        <>
          {callInformations.callActions.transfer && (
            <TransferTo sessionId={callInformations.id} />
          )}

          {callInformations.callActions.conference && (
            <ConferenceNumber sessionId={callInformations.id} />
          )}

          {callInformations.callActions.announcement &&
            callCurrentsMemo.length && (
              <NuacomComponent.Announcements sessionId={callInformations.id} />
            )}

          {isTerminated ? (
            <>
              <NuacomComponent.SendNotes
                callId={tempSessionId}
                open={addCallNotes}
                onClose={setAddCallNotes}
              />

              <NuacomComponent.AddTags
                callId={tempSessionId}
                open={addTags}
                onClose={setAddTags}
                callInfo={callInformations}
              />
            </>
          ) : (
            <>
              <NuacomComponent.SendNotes
                callId={tempSessionId}
                open={callInformations.callActions.notes}
                onClose={() => controlSessions(callInformations.id, 'notesOff')}
              />

              <NuacomComponent.AddTags
                callId={tempSessionId}
                open={callInformations.callActions.tags}
                onClose={() => controlSessions(callInformations.id, 'tagsOff')}
                callInfo={callInformations}
              />
            </>
          )}

          <DottedDivisor />
          <Space height={16} />

          <NuacomComponent.ShowTags callId={tempSessionId} />
          <NuacomComponent.ShowNotes callId={tempSessionId} />

          {/* Disable as result of NWA-1993 */}
          {/* <RecentContainer>
            {callCurrentsMemo.length < 2 &&
              location?.pathname?.includes('displayCall') && (
                <NuacomComponent.RecentCallHistory phoneNumber={numberFrom} />
              )}
          </RecentContainer> */}
        </>
      )}
    </>
  );
}
